// SpotifyTrack type definition
export interface SpotifyTrack {
  id: string;
  name: string;
  artists: Array<{ name: string; id: string }>;
  album: {
    name: string;
    images: Array<{ url: string; width: number; height: number }>;
  };
  duration_ms: number;
  preview_url: string | null;
  explicit: boolean;
  popularity: number;
  external_urls: {
    spotify: string;
  };
}

// SpotifyArtist type definition for detailed artist info
export interface SpotifyArtist {
  id: string;
  name: string;
  genres: string[];
  popularity: number;
  external_urls: {
    spotify: string;
  };
}

class SpotifyService {
  private clientId: string;
  private clientSecret: string;
  private accessToken: string | null;
  private tokenExpiry: number | null;

  constructor() {
    // Support both browser (Vite) and Node.js environments
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      this.clientId = import.meta.env.VITE_SPOTIFY_CLIENT_ID;
      this.clientSecret = import.meta.env.VITE_SPOTIFY_CLIENT_SECRET;
    } else {
      // Node.js environment (for scripts)
      this.clientId = process.env.VITE_SPOTIFY_CLIENT_ID || '';
      this.clientSecret = process.env.VITE_SPOTIFY_CLIENT_SECRET || '';
    }
    this.accessToken = null;
    this.tokenExpiry = null;
  }

  async getAccessToken() {
    // Check if we have a valid token
    if (this.accessToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
      return this.accessToken;
    }

    try {
      console.log('Getting new Spotify access token...');
      
      const credentials = btoa(`${this.clientId}:${this.clientSecret}`);
      
      const response = await fetch('https://accounts.spotify.com/api/token', {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: 'grant_type=client_credentials'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Spotify token error:', response.status, errorText);
        throw new Error(`Failed to get access token: ${response.status}`);
      }

      const data = await response.json();
      
      this.accessToken = data.access_token;
      // Set expiry to 90% of the actual expiry time for safety
      this.tokenExpiry = Date.now() + (data.expires_in * 1000 * 0.9);
      
      console.log('Got Spotify access token, expires in:', data.expires_in, 'seconds');
      return this.accessToken;
    } catch (error) {
      console.error('Error getting Spotify access token:', error);
      throw error;
    }
  }

  async searchTracks(query, limit = 10) {
    if (!query || query.trim().length === 0) {
      return [];
    }

    try {
      const token = await this.getAccessToken();
      
      const searchUrl = new URL('https://api.spotify.com/v1/search');
      searchUrl.searchParams.append('q', query.trim());
      searchUrl.searchParams.append('type', 'track');
      searchUrl.searchParams.append('limit', limit.toString());

      const response = await fetch(searchUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Spotify search error:', response.status, errorText);
        throw new Error(`Search failed: ${response.status}`);
      }

      const data = await response.json();
      
      return data.tracks.items.map(track => ({
        id: track.id,
        name: track.name,
        artists: (track.artists || []).map(artist => ({
          name: artist.name,
          id: artist.id
        })),
        album: {
          name: track.album?.name || 'Unknown Album',
          images: track.album?.images || []
        },
        duration_ms: track.duration_ms,
        preview_url: track.preview_url,
        explicit: track.explicit,
        popularity: track.popularity,
        external_urls: track.external_urls
      }));
    } catch (error) {
      console.error('Error searching Spotify:', error);
      throw error;
    }
  }

  async getArtist(artistId: string): Promise<SpotifyArtist | null> {
    console.log('🎤 SPOTIFY SERVICE - getArtist called with ID:', artistId);
    
    if (!artistId || artistId.trim().length === 0) {
      console.log('❌ Invalid artist ID provided');
      return null;
    }

    try {
      console.log('🔑 Getting access token...');
      const token = await this.getAccessToken();
      console.log('🔑 Access token obtained:', token ? 'YES' : 'NO');
      
      const url = `https://api.spotify.com/v1/artists/${artistId}`;
      console.log('📡 Making request to:', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Spotify artist fetch error:', response.status, errorText);
        return null;
      }

      const artist = await response.json();
      console.log('📡 Raw Spotify API response:', artist);
      console.log('🎵 Artist genres from API:', artist.genres);
      
      const processedArtist = {
        id: artist.id,
        name: artist.name,
        genres: artist.genres || [],
        popularity: artist.popularity,
        external_urls: artist.external_urls
      };
      
      console.log('✅ Processed artist data:', processedArtist);
      return processedArtist;
    } catch (error) {
      console.error('💥 Error fetching Spotify artist:', error);
      console.error('Error stack:', error.stack);
      return null;
    }
  }

  // Map Spotify genres to common database genres
  mapSpotifyGenreToDbGenre(spotifyGenres: string[]): string | null {
    const genreMapping: Record<string, string> = {
      // Country/Patriotic
      'country': 'Country',
      'country pop': 'Country', 
      'modern country rock': 'Country',
      'country road': 'Country',
      'classic country pop': 'Country',
      'outlaw country': 'Country',
      
      // Rock
      'rock': 'Rock',
      'classic rock': 'Rock',
      'hard rock': 'Rock',
      'soft rock': 'Rock',
      'pop rock': 'Rock',
      'alternative rock': 'Rock',
      'metal': 'Rock',
      'heavy metal': 'Rock',
      
      // Pop
      'pop': 'Pop',
      'dance pop': 'Pop',
      'electropop': 'Pop',
      'synthpop': 'Pop',
      'teen pop': 'Pop',
      
      // Classical
      'classical': 'Classical',
      'baroque': 'Classical',
      'romantic': 'Classical',
      'opera': 'Classical',
      'orchestral': 'Classical',
      
      // Folk
      'folk': 'Folk',
      'folk rock': 'Folk',
      'indie folk': 'Folk',
      'traditional folk': 'Folk',
      
      // Holiday/Christmas
      'christmas': 'Christmas',
      'holiday': 'Christmas',
      'christmas music': 'Christmas',
      
      // Jazz
      'jazz': 'Jazz',
      'smooth jazz': 'Jazz',
      'contemporary jazz': 'Jazz',
      'vocal jazz': 'Jazz',
      
      // R&B/Soul
      'r&b': 'R&B',
      'soul': 'R&B',
      'motown': 'R&B',
      'contemporary r&b': 'R&B',
      
      // Hip Hop
      'hip hop': 'Hip Hop',
      'rap': 'Hip Hop',
      'hip-hop': 'Hip Hop',
      
      // Electronic
      'electronic': 'Electronic',
      'edm': 'Electronic',
      'house': 'Electronic',
      'techno': 'Electronic',
      'dubstep': 'Electronic',
    };

    // Check each Spotify genre against our mapping
    for (const spotifyGenre of spotifyGenres) {
      const lowerGenre = spotifyGenre.toLowerCase();
      
      // Direct match
      if (genreMapping[lowerGenre]) {
        return genreMapping[lowerGenre];
      }
      
      // Partial match (if the Spotify genre contains our mapped genre)
      for (const [key, value] of Object.entries(genreMapping)) {
        if (lowerGenre.includes(key)) {
          return value;
        }
      }
    }

    return null;
  }

  async getTrack(trackId: string): Promise<SpotifyTrack | null> {
    if (!trackId || trackId.trim().length === 0) {
      return null;
    }

    try {
      const token = await this.getAccessToken();
      const response = await fetch(`https://api.spotify.com/v1/tracks/${encodeURIComponent(trackId)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Spotify track fetch error:', response.status, errorText);
        return null;
      }

      const track = await response.json();

      return {
        id: track.id,
        name: track.name,
        artists: (track.artists || []).map((artist: any) => ({
          name: artist.name,
          id: artist.id,
        })),
        album: {
          name: track.album?.name || 'Unknown Album',
          images: track.album?.images || [],
        },
        duration_ms: track.duration_ms,
        preview_url: track.preview_url,
        explicit: track.explicit,
        popularity: track.popularity,
        external_urls: track.external_urls,
      };
    } catch (error) {
      console.error('Error fetching Spotify track:', error);
      return null;
    }
  }
}

const spotifyService = new SpotifyService();

// Named export for the hook
export { spotifyService };
// Default export for other uses
export default spotifyService; 